import { formatToUSDate } from 'utils/dateTimeUtils/dateFormatUtil';

import { Wrapper } from '@american-autonomy/ag-ui';

import DetailsViewCardApplicationListItem from 'components/DetailsViewCardApplicationListItem';
import CaptionText from 'components/text/CaptionText';

import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { ApplicationResponse, DateGroup, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const groupByDate = (applications: ApplicationResponse[]): DateGroup[] =>
    applications.reduce<DateGroup[]>((groups, application) => {
        const dateSource = application.startTime ?? application.createdAt;
        const date = formatToUSDate(new Date(dateSource));
        const existingGroup = groups.find(group => group.date === date);

        if (existingGroup) {
            existingGroup.applications.push(application);
        } else {
            groups.push({ applications: [application], date });
        }

        return groups;
    }, []);

const DetailsViewCardApplicationList: FunctionComponent<Props> = ({
    applications,
    className = '',
    isLoading,
    onSelectApplication,
    selectedApplicationId,
    testId = TEST_ID
}) => {
    const { applicationDetailsPage } = usePageRoutes();
    const { noApplications } = useTranslation();

    if (isLoading || !applications) {
        return (
            <Wrapper
                className={`${className} ${styles.detailsViewCardApplicationList}`}
                testId={testId}>
                {Array.from({ length: 10 }).map((_, index) => (
                    <DetailsViewCardApplicationListItem isLoading key={index} />
                ))}
            </Wrapper>
        );
    }

    if (applications?.length > 0) {
        const dateGroups = groupByDate(applications);

        return (
            <Wrapper
                className={`${className} ${styles.detailsViewCardApplicationList}`}
                testId={testId}>
                {dateGroups.map(({ applications: groupApplications, date }) => (
                    <Wrapper className={styles.dateGroup} key={date}>
                        <CaptionText className={styles.dateHeader} text={date} />

                        {groupApplications.map(application => (
                            <DetailsViewCardApplicationListItem
                                application={application}
                                applicationRoute={applicationDetailsPage(application.id)}
                                isActive={application.id === selectedApplicationId}
                                key={application.id}
                                onClick={() => onSelectApplication?.(application)}
                            />
                        ))}
                    </Wrapper>
                ))}
            </Wrapper>
        );
    }

    return (
        <Wrapper
            className={`${className} ${styles.detailsViewCardApplicationList} ${styles.emptyState}`}
            testId={testId}>
            <CaptionText text={noApplications} />
        </Wrapper>
    );
};

export default DetailsViewCardApplicationList;
